<template>
  <NavbarHorizontalShell
    data_test="navbar-desktop-topbar"
    :aria_label="t('navbar.landmarks.employee.client.secondary')"
  >
    <NavbarHorizontalGreeting v-if="is_dashboard_route" />

    <div
      v-else-if="is_contacts_route && mq.isDesktop"
      class="flex min-w-0 flex-1 items-center gap-4 self-stretch overflow-hidden"
    >
      <AppLink
        v-for="key in contacts_tabs"
        :key="key"
        :to="{ name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: key } }"
        hide_underline
        :class="tabLinkClass(route.params.profile_type === key)"
        :data-test="`navbar-contact-${key.toLowerCase()}`"
      >
        {{ t(`contact.labels.${key.toLowerCase()}`) }}
      </AppLink>
    </div>

    <div
      v-else-if="is_settings_route && mq.isDesktop"
      class="flex min-w-0 flex-1 items-center gap-4 self-stretch overflow-hidden"
    >
      <AppLink
        v-for="tab in settings_tabs_visible"
        :key="tab.key"
        :to="{ name: tab.name }"
        hide_underline
        :class="tabLinkClass(route.name.startsWith(tab.name))"
        :data-test="`navbar-settings-${tab.key}`"
      >
        {{ t(tab.label) }}
      </AppLink>
    </div>

    <div
      v-else-if="show_tabs && mq.isDesktop"
      ref="container_ref"
      class="flex min-w-0 flex-1 items-center gap-4 self-stretch overflow-hidden"
    >
      <NavbarItemHorizontal
        v-for="(item, idx) in visible_items"
        :key="`v-${item.name}-${idx}`"
        class="shrink-0"
        :class="{ 'gap-2': item.prefix === 'employeesProjectInformation' }"
        :item="item"
        :params="params"
      >
        <NavbarProjectTabContent
          v-if="item.prefix === 'employeesProjectInformation'"
          :project="current_project"
        />
      </NavbarItemHorizontal>

      <button
        v-if="overflow_items.length > 0"
        ref="more_ref"
        type="button"
        class="flex shrink-0 cursor-pointer items-center px-2 text-gray-500 hover:text-gray-700"
        data-test="navbar-secondary-more"
        aria-haspopup="menu"
        @click="toggleMore"
      >
        <AppIcon name="regular-ellipsis" />
      </button>
      <PMenu
        ref="more_menu_ref"
        :model="overflow_items"
        :popup="true"
      >
        <template #item="{ item, props: item_props }">
          <NavbarItemHorizontal
            :item="item"
            :params="params"
            v-bind="item_props.action"
          />
        </template>
      </PMenu>

      <!-- Hidden measurement layer -->
      <div
        ref="measure_ref"
        aria-hidden="true"
        class="pointer-events-none invisible absolute -left-[9999px] flex items-center gap-4"
      >
        <NavbarItemHorizontal
          v-for="(item, idx) in routes_active"
          :key="`m-${item.name}-${idx}`"
          class="shrink-0"
          :class="{ 'gap-2': item.prefix === 'employeesProjectInformation' }"
          :item="item"
          :params="params"
          :expose_data_test="false"
        >
          <NavbarProjectTabContent
            v-if="item.prefix === 'employeesProjectInformation'"
            :project="current_project"
          />
        </NavbarItemHorizontal>
      </div>
    </div>

    <div v-else-if="show_tabs">
      <button
        type="button"
        class="cursor-pointer p-2 hover:text-gray-500"
        aria-haspopup="menu"
        @click="toggle"
      >
        <AppIcon
          name="regular-bars"
          size="lg"
        />
      </button>

      <PMenu
        ref="menu_ref"
        :model="bars_menu_items"
        :popup="true"
      >
        <template #item="{ item, props: item_props }">
          <AppLink
            hide_underline
            :to="item.to"
            :data-test="item.data_test"
            v-bind="item_props.action"
          >
            <span>{{ item.label }}</span>
          </AppLink>
        </template>
      </PMenu>
    </div>
  </NavbarHorizontalShell>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMqService } from '@/plugins/mq';
import {
  NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR,
  NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR,
} from '@/app/data/navbar_constants';
import { ROUTES_EMPLOYEE_OVERVIEWS, ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { FT } from '@/app/data/flipper_constants';
import NavbarItemHorizontal from '@/app/shared_components/navbar/NavbarItemHorizontal.vue';
import NavbarProjectTabContent from '@/app/_employees/components/navbar/NavbarProjectTabContent.vue';
import NavbarHorizontalShell from '@/app/shared_components/navbar/NavbarHorizontalShell.vue';
import NavbarHorizontalGreeting from '@/app/shared_components/navbar/NavbarHorizontalGreeting.vue';
import { mapGetters } from '@/store/lib/vue3helpers';

const GAP_PX = 16; // matches gap-4
const MORE_BUTTON_WIDTH_PX = 40;

const OVERVIEW_ROUTE_NAMES = new Set(Object.values(ROUTES_EMPLOYEE_OVERVIEWS));

const SETTINGS_TABS = [
  { key: 'tab_general', name: ROUTES_EMPLOYEES.CLIENT_COMPANY, label: 'client.labels.tab_general' },
  { key: 'tab_branding', name: ROUTES_EMPLOYEES.CLIENT_BRANDING, label: 'client.labels.tab_branding' },
  { key: 'tab_team', name: ROUTES_EMPLOYEES.SETTINGS_TEAM, label: 'navbar.topnavbar.team' },
  { key: 'tab_features', name: ROUTES_EMPLOYEES.CLIENT_SETTINGS, label: 'client.labels.tab_features' },
  {
    key: 'tab_billing',
    name: ROUTES_EMPLOYEES.CLIENT_SUBSCRIPTION,
    label: 'client.labels.tab_billing',
    owner_only: true,
  },
  { key: 'tab_integrations', name: ROUTES_EMPLOYEES.CLIENT_INTEGRATIONS, label: 'client.labels.tab_integrations' },
];

const TAB_BASE = 'flex shrink-0 items-center self-stretch border-b-2 transition-colors duration-150';
const TAB_ACTIVE = 'border-primary-400 font-semibold text-neutral-900';
const TAB_INACTIVE = 'border-transparent font-normal text-neutral-600 hover:border-neutral-300 hover:text-neutral-900';

const { t } = useI18n();
const route = useRoute();
const mq = useMqService();
const { proxy: app } = getCurrentInstance();

const { current_profile_is_employee_owner, current_project } = mapGetters([
  'current_profile_is_employee_owner',
  'current_project',
]);

const visible_count = ref(Infinity);

const container_ref = ref(null);
const measure_ref = ref(null);
const more_menu_ref = ref(null);
const menu_ref = ref(null);

let resize_observer = null;

const is_dashboard_route = computed(() => route.name === ROUTES_EMPLOYEES.CLIENT_DASHBOARD);

const is_all_projects_active = computed(() => OVERVIEW_ROUTE_NAMES.has(route.name));
const is_contacts_route = computed(() => route.name === ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE);
const is_settings_route = computed(() => SETTINGS_TABS.some((tab) => route.name.startsWith(tab.name)));
const settings_tabs_visible = computed(() =>
  SETTINGS_TABS.filter((tab) => !tab.owner_only || current_profile_is_employee_owner.value),
);

const contacts_tabs = computed(() => {
  const tabs = [PROFILE_TYPES.CUSTOMER];
  if (app.$flipper.enabled(FT.SALES)) tabs.push(PROFILE_TYPES.LEAD);
  if (app.$flipper.enabled(FT.DECISIONS_PARTNERS) || app.$flipper.enabled(FT.PROJECT_PARTNERS)) {
    tabs.push(PROFILE_TYPES.PARTNER);
  }
  return tabs;
});

const show_tabs = computed(
  () =>
    Boolean(current_project.value) ||
    is_all_projects_active.value ||
    is_contacts_route.value ||
    is_settings_route.value ||
    route.name === ROUTES_EMPLOYEES.CLIENT_TICKETS_SHOW,
);

function isFlipperEnabled(r) {
  return !r.flipper || app.$flipper.enabled(r.flipper);
}

function isRouteActive(r) {
  if (!isFlipperEnabled(r)) return false;
  if (!current_project.value) return true;
  return !current_project.value.attributes.skip_customer || !r.hidden_skip_customer;
}

const routes_active = computed(() => {
  const items = current_project.value
    ? NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR
    : NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR;
  return _.filter(items, isRouteActive);
});

const visible_items = computed(() => routes_active.value.slice(0, visible_count.value));
const overflow_items = computed(() => routes_active.value.slice(visible_count.value));

const bars_menu_items = computed(() => {
  if (is_contacts_route.value) {
    return contacts_tabs.value.map((key) => ({
      label: t(`contact.labels.${key.toLowerCase()}`),
      to: { name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: key } },
      data_test: `navbar-contact-${key.toLowerCase()}`,
    }));
  }
  if (is_settings_route.value) {
    return settings_tabs_visible.value.map((tab) => ({
      label: t(tab.label),
      to: { name: tab.name },
      data_test: `navbar-settings-${tab.key}`,
    }));
  }
  return routes_active.value.map((item) => ({
    label: t(item.title),
    to: { name: item.name, params: params.value },
    data_test: item.data_test,
  }));
});

const params = computed(() => {
  if (!current_project.value) return {};
  return {
    project_id: current_project.value.id,
    level_type: current_project.value.type,
    level_id: current_project.value.id,
  };
});

// determine the visible navbar items
function recomputeOverflow() {
  if (!container_ref.value || !measure_ref.value) return;
  const container_width = container_ref.value.clientWidth;
  const items = [...measure_ref.value.children];
  const widths = items.map((el) => el.offsetWidth);

  let count = 0;
  let used = 0;
  for (let i = 0; i < widths.length; i += 1) {
    const reserve = i < widths.length - 1 ? MORE_BUTTON_WIDTH_PX + GAP_PX : 0;
    const next_used = used + widths[i] + (i > 0 ? GAP_PX : 0);
    if (next_used + reserve > container_width) break;
    used = next_used;
    count += 1;
  }
  visible_count.value = count;
}

function toggle(event) {
  menu_ref.value.toggle(event);
}

function toggleMore(event) {
  more_menu_ref.value.toggle(event);
}

function tabLinkClass(is_active) {
  return `${TAB_BASE} ${is_active ? TAB_ACTIVE : TAB_INACTIVE}`;
}

watch(routes_active, () => {
  nextTick(recomputeOverflow);
});

watch(container_ref, (el) => {
  if (!resize_observer) return;
  if (el) {
    resize_observer.observe(el);
    nextTick(recomputeOverflow);
  }
});

onMounted(() => {
  if (mq.isDesktop) {
    resize_observer = new ResizeObserver(() => recomputeOverflow());
    if (container_ref.value) {
      resize_observer.observe(container_ref.value);
      nextTick(recomputeOverflow);
    }
  }
});

onBeforeUnmount(() => {
  if (resize_observer) resize_observer.disconnect();
});
</script>
